import { cn } from "@u/cn";
import { Panel } from "@ui/panel";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Inbox,
  Lock,
  SearchX,
  Sparkles,
  WifiOff,
  type LucideIcon,
} from "lucide-react";
import { type ReactNode } from "react";

/**
 * Every "this region has no normal content right now" moment.
 * One skeleton — Icon → Title → Description → Primary → Secondary — driven
 * by `variant` (tone + default icon + ARIA role). Replaces ad-hoc
 * Empty/Error/NoResults/Forbidden blocks. Presentational only: copy comes
 * from the caller (i18n at call site); actions are injected as <Button> nodes.
 *
 * NOT for: loading (use skeletons/spinner), transient confirmations (Sonner
 * toast), or inline field validation (react-hook-form). See CLAUDE.md §13.
 */
export type FeedbackVariant =
  | "empty"
  | "onboarding"
  | "no-results"
  | "error"
  | "warning"
  | "success"
  | "offline"
  | "forbidden";

type Tone = "neutral" | "brand" | "danger" | "warning" | "success";

interface VariantConfig {
  icon: LucideIcon;
  tone: Tone;
  /** ARIA live role. undefined = decorative (no announcement). */
  role?: "alert" | "status";
}

const VARIANTS: Record<FeedbackVariant, VariantConfig> = {
  empty: { icon: Inbox, tone: "neutral" },
  onboarding: { icon: Sparkles, tone: "brand" },
  "no-results": { icon: SearchX, tone: "neutral", role: "status" },
  error: { icon: AlertCircle, tone: "danger", role: "alert" },
  warning: { icon: AlertTriangle, tone: "warning", role: "status" },
  success: { icon: CheckCircle2, tone: "success", role: "status" },
  offline: { icon: WifiOff, tone: "warning", role: "status" },
  forbidden: { icon: Lock, tone: "neutral" },
};

// Tone → token classes. Never hex (CLAUDE.md §13). Tone is never the only
// signal — icon + text always carry the meaning (colorblind-safe).
const TONE_CLASS: Record<Tone, string> = {
  neutral: "text-muted-foreground bg-muted",
  brand: "text-primary bg-primary/10",
  danger: "text-destructive bg-destructive/10",
  warning: "text-warning bg-warning/10",
  success: "text-success-strong bg-success/10",
};

type Size = "sm" | "md" | "lg";

const SIZE = {
  sm: { circle: "w-10 h-10 mb-4", glyph: 20, title: "text-base" },
  md: { circle: "w-14 h-14 mb-6", glyph: 24, title: "text-lg" },
  lg: { circle: "w-16 h-16 mb-6", glyph: 28, title: "text-xl" },
} as const;

export interface FeedbackStateProps {
  variant: FeedbackVariant;
  title: string;
  description?: string;
  /** Override the variant's default icon (lucide or simple-icons glyph). */
  icon?: LucideIcon;
  /** Pass a <Button> node, not a label — caller owns intent/i18n/loading. */
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  size?: Size;
  /** "dashed" = page/section placeholder. "plain" = inline. "panel" = card. */
  surface?: "dashed" | "plain" | "panel";
  className?: string;
  "data-testid"?: string;
}

export function FeedbackState({
  variant,
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  size = "md",
  surface = "dashed",
  className,
  "data-testid": testId,
}: FeedbackStateProps) {
  const config = VARIANTS[variant];
  const Icon = icon ?? config.icon;
  const sz = SIZE[size];

  const inner = (
    <div
      role={config.role}
      data-variant={variant}
      data-testid={testId}
      className={cn(
        "flex flex-col items-center justify-center text-center",
        surface === "dashed" &&
          "py-16 md:py-32 px-6 border-dashed border-2 border-black/5 dark:border-white/5 rounded-4xl md:rounded-[4rem]",
        surface === "plain" && "py-12 px-6",
        className,
      )}
    >
      <div
        aria-hidden
        className={cn(
          "shrink-0 rounded-full flex items-center justify-center",
          sz.circle,
          TONE_CLASS[config.tone],
        )}
      >
        <Icon size={sz.glyph} strokeWidth={1.5} />
      </div>

      <h3 className={cn("font-medium", sz.title)}>{title}</h3>

      {description && (
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          {description}
        </p>
      )}

      {(primaryAction || secondaryAction) && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {primaryAction}
          {secondaryAction}
        </div>
      )}
    </div>
  );

  if (surface === "panel") {
    return <Panel className={cn("py-12 px-6", className)}>{inner}</Panel>;
  }

  return inner;
}
